import CategoryGroupIcon from '@/components/cashflow/CategoryGroupIcon';
import { useDrawer } from '@/components/common/DrawerManager';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { ChevronRight } from '@/components/common/icons';
import { LoadingTrs } from '@/components/common/LoadingTrs';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { EyebrowHeader, Text } from '@/components/common/Typography';
import { Center, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { CategoryGroupWithSpending } from '@common/react/budget';
import { toDollars } from '@common/util/currency';
import pluralize from '@common/util/pluralize';

interface BudgetCategoryGroupsTableProps {
    isEnabled: boolean;
    categoryGroups: CategoryGroupWithSpending[];
    loading: boolean;
}

export const BudgetCategoryGroupsTable = ({
    categoryGroups,
    isEnabled,
    loading,
}: BudgetCategoryGroupsTableProps) => {
    return (
        <FlexCol mt={8} gap={2}>
            <EyebrowHeader color="text">
                {categoryGroups.length} {isEnabled ? 'enabled' : 'disabled'}{' '}
                {pluralize('category', categoryGroups.length, false)}
            </EyebrowHeader>
            <Table>
                <Thead>
                    <Th>Category</Th>
                    {isEnabled && <Th>Budget</Th>}
                    <Th>Actual</Th>
                    {isEnabled && <Th>Remaining</Th>}
                    <Th></Th>
                </Thead>
                {loading ? (
                    <LoadingTrs
                        rowCount={categoryGroups.length}
                        columnCount={isEnabled ? 5 : 3}
                    />
                ) : (
                    <Tbody>
                        {categoryGroups.map((group) => (
                            <CategoryGroupRow
                                key={group.id}
                                group={group}
                                isEnabled={isEnabled}
                            />
                        ))}
                    </Tbody>
                )}
            </Table>
            {categoryGroups.length === 0 && (
                <Center mt={2}>
                    <Text color="textMuted">
                        {isEnabled
                            ? 'No categories have been enabled yet'
                            : 'All categories have been enabled'}
                    </Text>
                </Center>
            )}
        </FlexCol>
    );
};

const CategoryGroupRow = ({
    group,
    isEnabled,
}: {
    group: CategoryGroupWithSpending;
    isEnabled: boolean;
}) => {
    const { showDrawer } = useDrawer();

    return (
        <Tr
            key={group.id}
            {...hoverStyleProps({ bg: 'gray.50' })}
            onClick={() => {
                showDrawer({
                    drawerKey: 'budget-category-group-detail',
                    entityId: group.id,
                    trapFocus: false,
                });
            }}
        >
            <Td>
                <FlexRow align="center" gap={4}>
                    <CategoryGroupIcon categoryGroup={group} />
                    <Text>{group.name}</Text>
                </FlexRow>
            </Td>
            {isEnabled && <Td>{toDollars(group.budgetedAmount)}</Td>}
            <Td>{toDollars(group.spendingAmount)}</Td>
            {isEnabled && (
                <Td>
                    <Text color={group.isOver ? 'red.600' : 'green.800'}>
                        {toDollars(group.budgetedAmount - group.spendingAmount)}
                    </Text>
                </Td>
            )}
            <Td textAlign="right">
                <ChevronRight color="gray.600" size="sm" />
            </Td>
        </Tr>
    );
};
